import React, { useEffect, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { OperationPanelContext } from 'src/types/ViewModel/Stowage/OperationPanelContext';
import { CargoOperationViewModel } from 'src/types/ViewModel/Stowage/CargoOperation/CargoOperationViewModel';
import { CommoditySection } from './sections/CommoditySection';
import { useRotationStore } from 'src/context/RotationContext';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { OperationSection } from './sections/OperationSection';
import { PhysicalDataSection } from './sections/PhysicalDataSection';
import { CommentsSection } from './sections/CommentsSection';
import Decimal from 'decimal.js';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { calculateVolume, calculateVolumeFromLitreWeight, calculateWeight } from 'src/utils/stowage/stowageOperation/volumeUtils';
import {
    getCorrectedLitreWeightValue,
    getTotalContractWeight,
    getTotalNominatedVolume,
    getTotalNominatedWeight,
} from './utils/cargoOperationDisplayUtils';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { getCargoOperationForCargoAndOperationType, getBerthCalls, isManualCall, isValidPortCall } from 'src/utils/stowage/stowageRotationUtils';
import { isManualCargoOperation } from 'src/utils/stowage/cargoOperationUtils';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { SelectOption } from 'src/types/ViewModel/SelectOption';
import { Product } from 'src/types/ViewModel/Stowage/Product/Product';
import { shouldShowOptionMinusValue, shouldShowOptionPlusValue } from 'src/utils/stowage/cargoOptionUtils';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { updateAdjustedCargoTemperature } from 'src/utils/stowage/stowageOperation/adjustedCargoTemperatureUtils';
import { convertToNumber, updateStateAndValidate, isEmptyObject, unloadCargoOperation } from './utils/cargoOperationUtils';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { createCargoOperationChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoOperationChangeDataFactory';
import { createCargoChangeData } from 'src/factory/Stowage/StowagePlanChanges/StowageChangeData/CargoChangeDataFactory';
import { CargoSection } from './sections/CargoSection';
import { getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import { roundTo3Decimals } from 'src/utils/numberHelper';

interface Props {
    cargoPanelContext: OperationPanelContext;
}

const CargoOperationPanel: React.FC<Props> = ({ cargoPanelContext }) => {
    const [cargoOperationVM, setCargoOperationVM] = useState<CargoOperationViewModel | null>(null);
    const [isNewCargoOperation, setIsNewCargoOperation] = useState<boolean>(false);
    const [isHeatingSectionVisible, setIsHeatingSectionVisible] = useState<boolean>(false);
    const { activeStowagePlan, setIsCargoOperationPanelOpen, setIsTradeNameSelectionListOpen } = useStowageStore();
    const { stowageRotationTree } = useRotationStore();
    const { saveStowageData } = usePostStowageApi();

    // Add new state for property changes
    const [operationPropertyChanges, setOperationPropertyChanges] = useState<any>({});
    const [cargoPropertyChanges, setCargoPropertyChanges] = useState<any>({});
    const [cargoTemperaturePropertyChanges, setCargoTemperaturePropertyChanges] = useState<any>({});

    useEffect(() => {
        if (!activeStowagePlan) return;

        if (cargoPanelContext.cargoOperation) {
            setInitialCargoOperation(cargoPanelContext.cargoOperation);
        } else {
            setInitialCargoOperation(null);
            setIsNewCargoOperation(true);
        }
    }, []);

    const setInitialCargoOperation = (cargoOperation: RotationCargoOperation) => {
        let initialCargoOperationVM: CargoOperationViewModel = {} as CargoOperationViewModel;
        let initialCargoVM: RotationCargo = {} as RotationCargo;

        if (cargoOperation) {
            const cargoTemperature = getCargoTemperature(activeStowagePlan, cargoOperation.cargo);
            cargoOperation.cargo.temperature = cargoTemperature;

            const initialNominatedVolume = calculateVolume(new Decimal(cargoOperation.nominatedWeight), cargoOperation.cargo, cargoTemperature);

            // Set initial operation values
            initialCargoOperationVM = {
                cargoOperationGuid: cargoOperation.cargoOperationGuid,
                portCallGuid: cargoOperation.portCallGuid,
                berthCallGuid: cargoOperation.berthCallGuid,
                cargoGuid: cargoOperation.cargoGuid,
                imosCargoOperationId: cargoOperation.imosCargoOperationId,
                imosCargoId: cargoOperation.imosCargoId,
                berthCall: cargoOperation.berthCall,
                cargo: { ...cargoOperation.cargo },
                cargoOperationSequenceNumber: cargoOperation.cargoOperationSequenceNumber,
                billOfLadingWeight: cargoOperation.billOfLadingWeight,
                originalContractWeight: cargoPanelContext.cargoOperation.contractWeight,
                contractWeight: cargoOperation.contractWeight,
                originalNominatedWeight: cargoPanelContext.cargoOperation.nominatedWeight,
                originalNominatedVolume: initialNominatedVolume,
                nominatedWeight: cargoOperation.nominatedWeight,
                nominatedVolume: initialNominatedVolume.toNumber(),
                totalContractWeight: 0,
                totalNominatedWeight: 0,
                totalNominatedVolume: 0,
                totalSwingFromWeight: 0,
                totalSwingToWeight: 0,
                operationType: cargoOperation.operationType,
                dataSource: cargoOperation.dataSource,
                hasMaxTotalNominatedWeight: true,
            };

            initialCargoVM = { ...cargoOperation.cargo };

            if (cargoOperation.cargo.productChanges) {
                initialCargoOperationVM.cargo.productChanges = { ...cargoOperation.cargo.productChanges };
            } else {
                initialCargoOperationVM.cargo.productChanges = null;
            }

            const _unloadingBerthCall = getUnloadingBerthCall(activeStowagePlan, initialCargoOperationVM.cargo);

            if (_unloadingBerthCall) {
                initialCargoOperationVM.unloadingBerthCall = _unloadingBerthCall;
                initialCargoOperationVM.unloadingPortCall = _unloadingBerthCall.portCall;
            }

            // Update totals
            initialCargoOperationVM.totalContractWeight = getTotalContractWeight(initialCargoOperationVM, activeStowagePlan);
            initialCargoOperationVM.totalNominatedWeight = getTotalNominatedWeight(initialCargoOperationVM, activeStowagePlan);
            initialCargoOperationVM.totalNominatedVolume = getTotalNominatedVolume(initialCargoOperationVM, activeStowagePlan);
            updateTotalSwingFromValue(initialCargoOperationVM);
            updateTotalSwingToValue(initialCargoOperationVM);
        } else {
            // Handle new cargo operation
            const voyageNr = cargoPanelContext.berthCall.portCall.voyageNumber;
            const _cargoOperations = stowageRotationTree.flatMap((portCall) => portCall.berthCalls.flatMap((berthCall) => berthCall.cargoOperations));
            const _cargoOperationsForVoyage = _cargoOperations.filter((co) => co.cargo.voyageNumber === voyageNr && isManualCall(co));

            const _maxCargoNumber =
                _cargoOperationsForVoyage?.length > 0 ? Math.max(..._cargoOperationsForVoyage.map((co) => co.cargo.cargoNumber)) : 0;

            initialCargoVM = {
                voyageNumber: voyageNr,
                cargoNumber: _maxCargoNumber + 1,
                cargoGuid: crypto.randomUUID(),
                dataSource: 'OrcaWeb',
                optionType: 'MOLCO',
                optionMinusPercentage: 0,
                optionPlusPercentage: 0,
                comment: '',
            } as RotationCargo;

            const operationGuid = crypto.randomUUID();

            initialCargoOperationVM = {
                cargoOperationGuid: operationGuid,
                portCallGuid: cargoPanelContext.berthCall.portCall.portCallGuid,
                berthCallGuid: cargoPanelContext.berthCall.berthCallGuid,
                imosCargoOperationId: null,
                imosCargoId: null,
                berthCall: cargoPanelContext.berthCall,
                cargoGuid: initialCargoVM.cargoGuid,
                cargo: initialCargoVM,
                cargoOperationSequenceNumber: 0,
                billOfLadingWeight: null,
                originalContractWeight: null,
                contractWeight: 0,
                originalNominatedWeight: null,
                originalNominatedVolume: null,
                nominatedWeight: 0,
                totalContractWeight: 0,
                totalNominatedWeight: 0,
                totalNominatedVolume: 0,
                totalSwingFromWeight: 0,
                totalSwingToWeight: 0,
                operationType: 'Loading',
                dataSource: 'OrcaWeb',
                hasMaxTotalNominatedWeight: true,
            } as CargoOperationViewModel;

            cargoPropertyChanges.cargoGuid = initialCargoVM.cargoGuid;
            operationPropertyChanges.cargoOperationGuid = initialCargoOperationVM.cargoOperationGuid;
            setCargoPropertyChanges({ ...cargoPropertyChanges });
            setOperationPropertyChanges({ ...operationPropertyChanges });
        }

        setCargoOperationVM({ ...initialCargoOperationVM });
    };

    const onCancel = () => {
        setIsTradeNameSelectionListOpen(false);
        setIsCargoOperationPanelOpen({ cargoOperation: null, berthCall: null, visible: false });
    };

    const onSave = async () => {
        const stowagePlanChanges: StowagePlanChange[] = [];

        // Handle new or existing cargo operation changes
        const operationChanges = isNewCargoOperation ? createNewCargoOperationChanges() : createExistingCargoOperationChanges();
        stowagePlanChanges.push(...operationChanges);

        // Add unloading operation if needed (only for new cargo operations)
        if (isNewCargoOperation && cargoOperationVM.unloadingBerthCall && isManualCargoOperation(cargoOperationVM)) {
            const unloadStowagePlanChanges = unloadCargoOperation(activeStowagePlan, cargoOperationVM);
            if (unloadStowagePlanChanges) {
                stowagePlanChanges.push(...unloadStowagePlanChanges);
            }
        }

        // For existing cargo operations, update existing unloading operation(s) if they exist
        if (!isNewCargoOperation && isManualCargoOperation(cargoOperationVM)) {
            const existingUnloadingOperations = getCargoOperationForCargoAndOperationType(activeStowagePlan, cargoOperationVM.cargo, 'Unloading');

            if (existingUnloadingOperations && existingUnloadingOperations.length > 0) {
                // Handle individual unloading operation changes
                Object.keys(unloadingOperationChanges).forEach((operationGuid) => {
                    const changes = unloadingOperationChanges[operationGuid];
                    stowagePlanChanges.push(
                        createStowagePlanChange(
                            activeStowagePlan.stowagePlanGuid,
                            operationGuid as `${string}-${string}-${string}-${string}-${string}`,
                            StowageChangeDataType.RotationCargoOperation,
                            changes
                        )
                    );
                });

                // Handle single unloading operation change (for backward compatibility)
                if (
                    existingUnloadingOperations.length === 1 &&
                    cargoOperationVM.unloadingBerthCall &&
                    Object.keys(unloadingOperationChanges).length === 0
                ) {
                    const unloadingOpChanges = {
                        portCallGuid: cargoOperationVM.unloadingPortCall?.portCallGuid,
                        berthCallGuid: cargoOperationVM.unloadingBerthCall?.berthCallGuid,
                    };

                    stowagePlanChanges.push(
                        createStowagePlanChange(
                            activeStowagePlan.stowagePlanGuid,
                            existingUnloadingOperations[0].cargoOperationGuid,
                            StowageChangeDataType.RotationCargoOperation,
                            unloadingOpChanges
                        )
                    );
                }

                // Propagate weight changes from loading operation to all unloading operations
                const hasWeightChanges =
                    operationPropertyChanges.contractWeight !== undefined || operationPropertyChanges.nominatedWeight !== undefined;
                if (hasWeightChanges) {
                    existingUnloadingOperations.forEach((unloadingOp) => {
                        const weightChanges: any = {};

                        // Propagate contract weight changes
                        if (operationPropertyChanges.contractWeight !== undefined) {
                            weightChanges.contractWeight = operationPropertyChanges.contractWeight;
                        }

                        // Propagate nominated weight changes
                        if (operationPropertyChanges.nominatedWeight !== undefined) {
                            weightChanges.nominatedWeight = operationPropertyChanges.nominatedWeight;
                        }

                        stowagePlanChanges.push(
                            createStowagePlanChange(
                                activeStowagePlan.stowagePlanGuid,
                                unloadingOp.cargoOperationGuid,
                                StowageChangeDataType.RotationCargoOperation,
                                weightChanges
                            )
                        );
                    });
                }
            }
        }

        // Handle product changes
        if (cargoOperationVM.cargo?.productChanges && !isEmptyObject(cargoOperationVM.cargo?.productChanges)) {
            cargoPropertyChanges.productChanges = cargoOperationVM.cargo.productChanges;
        }

        // Handle temperature changes
        const temperatureChange = createCargoTemperatureChange();
        if (temperatureChange) {
            stowagePlanChanges.push(temperatureChange);
        }

        await saveStowageData(activeStowagePlan, stowagePlanChanges);
        setIsCargoOperationPanelOpen({ cargoOperation: null, berthCall: null, visible: false });
    };

    const isFormDataInvalid = (): boolean => {
        return !cargoOperationVM || !cargoOperationVM.cargo || !cargoOperationVM.cargo.product || !cargoOperationVM.contractWeight;
    };

    const toggleHeatingSectionVisible = () => {
        setIsHeatingSectionVisible(!isHeatingSectionVisible);
    };

    const isFieldReadOnly = (fieldName: string): boolean => {
        const ALWAYS_EDITABLE_FIELDS = [
            'litreWeightReferenceTemperature',
            'litreWeight',
            'litreWeightCorrectionFactor',
            'cargoTemperature',
            'maxAdjacentTemperature',
            'loadingTemperatureRangeMax',
            'voyageTemperatureRangeMin',
            'voyageTemperatureRangeMax',
            'unloadingTemperatureRangeMax',
            'comment',
        ];
        const ORCAWEB_RESTRICTED_FIELDS = ['optionPlusPercentage', 'optionMinusPercentage'];

        if (ALWAYS_EDITABLE_FIELDS.includes(fieldName)) {
            return false;
        }

        if (isManualCall(cargoOperationVM)) {
            if (!ORCAWEB_RESTRICTED_FIELDS.includes(fieldName)) {
                return false;
            }

            if (fieldName === 'optionMinusPercentage') {
                return !shouldShowOptionMinusValue(cargoOperationVM.cargo);
            }
            if (fieldName === 'optionPlusPercentage') {
                return !shouldShowOptionPlusValue(cargoOperationVM.cargo);
            }
        }

        return true;
    };

    const getUpcomingPortCalls = (): RotationPortCall[] => {
        if (!stowageRotationTree || !cargoOperationVM?.berthCallGuid) {
            return [];
        }

        // Filter stowageRotationTree to only include port calls with IMOS function 'M'
        const validRotationTree = stowageRotationTree.filter((portCall) => isValidPortCall(portCall));

        let startPortCallIndex = -1;
        let startBerthCallIndex = -1;

        validRotationTree.forEach((portCall, portIndex) => {
            const berthIndex = portCall.berthCalls?.findIndex((berth) => berth.berthCallGuid === cargoOperationVM.berthCallGuid);

            if (berthIndex !== -1 && berthIndex !== undefined) {
                startPortCallIndex = portIndex;
                startBerthCallIndex = berthIndex;
            }
        });

        if (startPortCallIndex === -1) {
            return [];
        }

        const _isLastBerthInPort = startBerthCallIndex === validRotationTree[startPortCallIndex].berthCalls.length - 1;

        if (_isLastBerthInPort) {
            return validRotationTree.slice(startPortCallIndex + 1).map((portCall) => ({
                ...portCall,
                berthCalls: [...(portCall.berthCalls || [])],
            }));
        }

        const _upcomingPortCalls = validRotationTree.slice(startPortCallIndex).map((portCall) => ({
            ...portCall,
            berthCalls: [...(portCall.berthCalls || [])],
        }));

        if (startBerthCallIndex !== -1 && _upcomingPortCalls.length > 0) {
            _upcomingPortCalls[0].berthCalls = _upcomingPortCalls[0].berthCalls?.slice(startBerthCallIndex + 1) || [];

            if (_upcomingPortCalls[0].berthCalls.length === 0) {
                _upcomingPortCalls.shift();
            }
        }

        return _upcomingPortCalls.filter((portCall) => {
            const berthCalls = getBerthCalls(portCall);
            return berthCalls && berthCalls.length > 0;
        });
    };

    const getUnloadingOperations = (stowagePlan: StowagePlan, cargo: RotationCargo): RotationCargoOperation[] => {
        const _unloadingCargoOperations = getCargoOperationForCargoAndOperationType(stowagePlan, cargo, 'Unloading');
        return _unloadingCargoOperations || [];
    };

    const getUnloadingBerthCall = (stowagePlan: StowagePlan, cargo: RotationCargo): RotationBerthCall => {
        const _unloadingCargoOperations = getCargoOperationForCargoAndOperationType(stowagePlan, cargo, 'Unloading');
        if (_unloadingCargoOperations && _unloadingCargoOperations.length > 0) {
            return _unloadingCargoOperations[0].berthCall;
        }
        return null;
    };

    const getUnloadingCall = () => {
        if (cargoOperationVM.unloadingPortCall && cargoOperationVM.unloadingBerthCall) {
            return `${cargoOperationVM.unloadingPortCall.portDisplayName} - ${cargoOperationVM.unloadingBerthCall.berthName}`;
        } else if (cargoOperationVM.unloadingPortCall && !cargoOperationVM.unloadingBerthCall) {
            return cargoOperationVM.unloadingPortCall.portDisplayName;
        }
        return '';
    };

    const getVoyageTemeratureMax = (dataObject: any): number => {
        if (dataObject?.productChanges?.voyageTemperatureRange) {
            return dataObject.productChanges.voyageTemperatureRange.max;
        } else {
            return dataObject.product.voyageTemperatureRange.max;
        }
    };

    const [unloadingOperationChanges, setUnloadingOperationChanges] = useState<{
        [operationGuid: string]: { portCallGuid: string; berthCallGuid: string };
    }>({});

    const getSelectedItemForOperation = (operation: RotationCargoOperation): string => {
        const changes = unloadingOperationChanges[operation.cargoOperationGuid];
        if (changes) {
            // Find the port and berth call from the changes
            const upcomingPortCalls = getUpcomingPortCalls();
            const portCall = upcomingPortCalls.find((pc) => pc.portCallGuid === changes.portCallGuid);
            if (portCall) {
                const berthCall = portCall.berthCalls?.find((bc) => bc.berthCallGuid === changes.berthCallGuid);
                if (berthCall) {
                    return `${portCall.portDisplayName} - ${berthCall.berthName}`;
                }
                return portCall.portDisplayName;
            }
        }

        // Fallback to current assignment
        if (operation.berthCall) {
            return `${operation.berthCall.portCall.portDisplayName} - ${operation.berthCall.berthName}`;
        }

        return 'Select port call';
    };

    const onSelectUnloadingPortForOperation = (operationGuid: string, portCall: RotationPortCall, berthCall: RotationBerthCall) => {
        if (!portCall) return;

        const berthCallToUse = berthCall || getBerthCalls(portCall)?.[0];
        if (!berthCallToUse) return;

        // Track the change for this specific operation
        unloadingOperationChanges[operationGuid] = {
            portCallGuid: portCall.portCallGuid,
            berthCallGuid: berthCallToUse.berthCallGuid,
        };

        setUnloadingOperationChanges({ ...unloadingOperationChanges });
        setCargoOperationVM({ ...cargoOperationVM });
    };

    const onSelectUnloadingPort = (portCall: RotationPortCall, berthCall: RotationBerthCall) => {
        // If portCall is null, clear the selection
        if (!portCall) {
            cargoOperationVM.unloadingPortCall = null;
            cargoOperationVM.unloadingBerthCall = null;
        } else {
            cargoOperationVM.unloadingPortCall = portCall;

            // If no berth call is provided, automatically select the first berth call in the port
            if (!berthCall) {
                const berthCalls = getBerthCalls(portCall);
                if (berthCalls && berthCalls.length > 0) {
                    cargoOperationVM.unloadingBerthCall = berthCalls[0];
                } else {
                    cargoOperationVM.unloadingBerthCall = null;
                }
            } else {
                cargoOperationVM.unloadingBerthCall = berthCall;
            }
        }

        setCargoOperationVM({ ...cargoOperationVM });
    };

    const onTradeNameChange = (e: SelectOption, product: Product) => {
        if (product) {
            cargoOperationVM.cargo.product = product;
            cargoOperationVM.cargo.productChanges = null;
            cargoOperationVM.cargo.productId = product.id;
            cargoOperationVM.cargo.chartererName = product.chartererName;
            cargoOperationVM.cargo.temperature = product.voyageTemperatureRange.max;
            cargoPropertyChanges.productId = product.id;
            setCargoPropertyChanges({ ...cargoPropertyChanges });
            setIsTradeNameSelectionListOpen(false);
            updateNominatedVolumeByWeight(cargoOperationVM);
        }
    };

    const onProductInputChange = (changeObject: InputChangeData) => {
        try {
            if (!cargoPropertyChanges.productChanges) cargoPropertyChanges.productChanges = {};
            if (!changeObject.dataObject.productChanges) changeObject.dataObject.productChanges = {};

            const temperatureRangeHandlers = {
                loadingTemperatureRangeMax: { field: 'loadingTemperatureRange', property: 'max' },
                voyageTemperatureRangeMin: { field: 'voyageTemperatureRange', property: 'min' },
                voyageTemperatureRangeMax: { field: 'voyageTemperatureRange', property: 'max' },
                unloadingTemperatureRangeMax: { field: 'unloadingTemperatureRange', property: 'max' },
            };

            const singleValueHandlers = {
                litreWeightReferenceTemperature: true,
                litreWeight: true,
                litreWeightCorrectionFactor: true,
                maxAdjacentTemperature: true,
            };

            const { name, value } = changeObject.event.target;
            const numericValue = convertToNumber(value);

            if (name in temperatureRangeHandlers) {
                const { field, property } = temperatureRangeHandlers[name as keyof typeof temperatureRangeHandlers];

                changeObject.dataObject.productChanges[field] = changeObject.dataObject.productChanges[field] || {};
                cargoPropertyChanges.productChanges[field] = cargoPropertyChanges.productChanges[field] || {};

                if (changeObject.dataObject.product[field][property] === numericValue || value === '') {
                    if (changeObject.dataObject.productChanges[field]) {
                        changeObject.dataObject.productChanges[field][property] = null;

                        if (Object.keys(changeObject.dataObject.productChanges[field]).length === 0) {
                            changeObject.dataObject.productChanges[field] = null;
                        }
                    }

                    if (cargoPropertyChanges.productChanges[field]) {
                        if (Object.keys(cargoPropertyChanges.productChanges[field]).length === 0) {
                            cargoPropertyChanges.productChanges[field] = null;
                        }
                    }
                } else {
                    changeObject.dataObject.productChanges[field][property] = numericValue;
                    cargoPropertyChanges.productChanges[field][property] = numericValue;
                }
            } else if (name in singleValueHandlers) {
                if (changeObject.dataObject.product[name] === numericValue || value === '') {
                    changeObject.dataObject.productChanges[name] = null;
                    cargoPropertyChanges.productChanges[name] = null;
                } else {
                    changeObject.dataObject.productChanges[name] = numericValue;
                    cargoPropertyChanges.productChanges[name] = numericValue;
                }
            } else {
                console.log('Unhandled product input change:', name);
            }

            if (Object.keys(cargoPropertyChanges.productChanges).length === 0) {
                cargoPropertyChanges.productChanges = null;
            }

            if (Object.keys(changeObject.dataObject.productChanges).length === 0) {
                changeObject.dataObject.productChanges = null;
            }

            setCargoPropertyChanges({ ...cargoPropertyChanges });
            updateStateAndValidate(cargoOperationVM, name, changeObject.isValid, setCargoOperationVM);
        } catch (error) {
            console.error('Error in onProductInputChange:', error);
        }
    };

    const onCargoInputChange = (changeObject: InputChangeData) => {
        try {
            const { name, value } = changeObject.event.target;
            const { dataObject } = changeObject;

            const updateStateObjects = (propertyName: string, newValue: string | number) => {
                dataObject[propertyName] = newValue;
                cargoPropertyChanges[propertyName] = newValue;
            };

            if (name === 'comment') {
                cargoOperationVM.cargo.comment = value;
                cargoPropertyChanges['comment'] = value;
            } else if (name === 'cargoTemperature') {
                const numericValue = convertToNumber(value);
                if (dataObject.temperature === numericValue || value === '') {
                    cargoTemperaturePropertyChanges.temperature = getVoyageTemeratureMax(dataObject);
                    dataObject.temperature = getVoyageTemeratureMax(dataObject);
                } else {
                    cargoTemperaturePropertyChanges.temperature = numericValue;
                    dataObject.temperature = numericValue;
                }

                setCargoTemperaturePropertyChanges({ ...cargoTemperaturePropertyChanges });
            } else {
                if (name === 'optionMinusPercentage') {
                    // parse as number and invert to negative
                    const parsedValue = convertToNumber(value);
                    const invertedValue = parsedValue > 0 ? -parsedValue : parsedValue;
                    updateStateObjects(name, invertedValue);
                } else if (name !== 'optionType') {
                    updateStateObjects(name, convertToNumber(value));
                } else {
                    updateStateObjects(name, value || '');
                }

                if (name === 'optionType' && (value === 'MOO' || value === 'MCO')) {
                    cargoOperationVM.cargo.optionMinusPercentage = 0;
                }

                if (name === 'optionType' && (value === 'LOO' || value === 'LCO')) {
                    cargoOperationVM.cargo.optionPlusPercentage = 0;
                }

                if (name === 'optionType' || name === 'optionMinusPercentage' || name === 'optionPlusPercentage') {
                    updateNominatedWeight(cargoOperationVM);
                    updateNominatedVolumeByWeight(cargoOperationVM);
                    updateTotalNominatedWeight(cargoOperationVM);
                    updateTotalSwingFromValue(cargoOperationVM);
                    updateTotalSwingToValue(cargoOperationVM);
                    checkMaxTotalNominatedWeight(cargoOperationVM);
                }
            }

            setCargoPropertyChanges({ ...cargoPropertyChanges });
            updateStateAndValidate(cargoOperationVM, name, changeObject.isValid, setCargoOperationVM);
        } catch (error) {
            console.error('Error in onCargoInputChange:', error);
        }
    };

    const onOperationInputChange = (changeObject: InputChangeData) => {
        try {
            const { name, value } = changeObject.event.target;
            const numericValue = convertToNumber(value);

            changeObject.dataObject[name] = numericValue;
            operationPropertyChanges[name] = numericValue;

            const updateHandlers = {
                contractWeight: () => {
                    updateContractWeight(cargoOperationVM);
                },
                nominatedWeight: () => {
                    updateNominatedWeightValues(cargoOperationVM);
                },
                nominatedVolume: () => {
                    updateNominatedVolumeValues(cargoOperationVM);
                },
            };

            if (name in updateHandlers) {
                updateHandlers[name as keyof typeof updateHandlers]();
            }

            setOperationPropertyChanges({ ...operationPropertyChanges });
            updateStateAndValidate(cargoOperationVM, name, changeObject.isValid, setCargoOperationVM);
        } catch (error) {
            console.error('Error in onOperationInputChange:', error);
        }
    };

    const updateContractWeight = (cargoOperationVM: CargoOperationViewModel) => {
        updateNominatedWeight(cargoOperationVM);
        updateNominatedVolumeByWeight(cargoOperationVM);
        cargoOperationVM.totalContractWeight = getTotalContractWeight(cargoOperationVM, activeStowagePlan);
        cargoOperationVM.totalNominatedWeight = getTotalNominatedWeight(cargoOperationVM, activeStowagePlan);
        updateTotalSwingFromValue(cargoOperationVM);
        updateTotalSwingToValue(cargoOperationVM);
    };

    const updateNominatedWeight = (cargoOperationVM: CargoOperationViewModel) => {
        if (cargoOperationVM.cargo.optionType !== 'Min / Max') {
            cargoOperationVM.nominatedWeight = roundTo3Decimals(
                cargoOperationVM.contractWeight * (1 + Math.abs(cargoOperationVM.cargo.optionPlusPercentage) / 100)
            );
        } else {
            cargoOperationVM.nominatedWeight = roundTo3Decimals(cargoOperationVM.contractWeight + cargoOperationVM.cargo.optionPlusPercentage);
        }
        operationPropertyChanges['nominatedWeight'] = cargoOperationVM.nominatedWeight;
    };

    const updateNominatedWeightValues = (cargoOperationVM: CargoOperationViewModel) => {
        updateTotalNominatedWeight(cargoOperationVM);
        updateNominatedVolumeByWeight(cargoOperationVM);
        checkMaxTotalNominatedWeight(cargoOperationVM);
    };

    const checkMaxTotalNominatedWeight = (cargoOperationVM: CargoOperationViewModel) => {
        if (cargoOperationVM.totalNominatedWeight < cargoOperationVM.totalSwingToWeight) {
            cargoOperationVM.hasMaxTotalNominatedWeight = false;
        } else {
            cargoOperationVM.hasMaxTotalNominatedWeight = true;
        }
    };

    const updateTotalNominatedWeight = (cargoOperationVM: CargoOperationViewModel) => {
        cargoOperationVM.totalNominatedWeight = getTotalNominatedWeight(cargoOperationVM, activeStowagePlan);
    };

    const updateNominatedVolumeValues = (cargoOperationVM: CargoOperationViewModel) => {
        updateNominatedWeightByVolume(cargoOperationVM);
    };

    const updateNominatedWeightByVolume = (cargoOperationVM: CargoOperationViewModel) => {
        cargoOperationVM.nominatedWeight = roundTo3Decimals(
            calculateWeight(
                new Decimal(cargoOperationVM.nominatedVolume),
                cargoOperationVM.cargo,
                getCargoTemperature(activeStowagePlan, cargoOperationVM.cargo)
            ).toNumber()
        );
    };

    const updateTotalSwingFromValue = (cargoOperationVM: CargoOperationViewModel) => {
        if (cargoOperationVM.cargo.optionType !== 'Min / Max') {
            cargoOperationVM.totalSwingFromWeight =
                getTotalContractWeight(cargoOperationVM, activeStowagePlan) * (1 - Math.abs(cargoOperationVM.cargo.optionMinusPercentage) / 100);
        } else {
            cargoOperationVM.totalSwingFromWeight =
                getTotalContractWeight(cargoOperationVM, activeStowagePlan) - cargoOperationVM.cargo.optionMinusPercentage;
        }
    };

    const updateTotalSwingToValue = (cargoOperationVM: CargoOperationViewModel) => {
        if (cargoOperationVM.cargo.optionType !== 'Min / Max') {
            cargoOperationVM.totalSwingToWeight =
                getTotalContractWeight(cargoOperationVM, activeStowagePlan) * (1 + Math.abs(cargoOperationVM.cargo.optionPlusPercentage) / 100);
        } else {
            cargoOperationVM.totalSwingToWeight =
                getTotalContractWeight(cargoOperationVM, activeStowagePlan) + cargoOperationVM.cargo.optionPlusPercentage;
        }
    };

    const updateNominatedVolumeByWeight = (cargoOperationVM: CargoOperationViewModel) => {
        if (cargoOperationVM.cargo.product) {
            const correctedLitreWeight = getCorrectedLitreWeightValue(cargoOperationVM);
            const nominatedVolume = calculateVolumeFromLitreWeight(
                new Decimal(cargoOperationVM.nominatedWeight),
                new Decimal(correctedLitreWeight)
            ).toNumber();

            cargoOperationVM.nominatedVolume = nominatedVolume;
        }

        const totalNominatedVolume = getTotalNominatedVolume(cargoOperationVM, activeStowagePlan);
        cargoOperationVM.totalNominatedVolume = totalNominatedVolume;
    };

    const createNewCargoOperationChanges = (): StowagePlanChange[] => {
        const changes: StowagePlanChange[] = [];

        const cargoOperationChangeData = createCargoOperationChangeData(cargoOperationVM);
        changes.push(
            createStowagePlanChange(
                activeStowagePlan.stowagePlanGuid,
                cargoOperationVM.cargoOperationGuid,
                StowageChangeDataType.RotationCargoOperation,
                cargoOperationChangeData
            )
        );

        const cargoChangeData = createCargoChangeData(cargoOperationVM.cargo);
        changes.push(
            createStowagePlanChange(
                activeStowagePlan.stowagePlanGuid,
                cargoOperationVM.cargoGuid,
                StowageChangeDataType.RotationCargo,
                cargoChangeData
            )
        );

        return changes;
    };

    const createExistingCargoOperationChanges = (): StowagePlanChange[] => {
        const changes: StowagePlanChange[] = [];

        if (!isEmptyObject(operationPropertyChanges)) {
            changes.push(
                createStowagePlanChange(
                    activeStowagePlan.stowagePlanGuid,
                    cargoOperationVM.cargoOperationGuid,
                    StowageChangeDataType.RotationCargoOperation,
                    operationPropertyChanges
                )
            );
        }

        if (!isEmptyObject(cargoPropertyChanges)) {
            changes.push(
                createStowagePlanChange(
                    activeStowagePlan.stowagePlanGuid,
                    cargoOperationVM.cargo.cargoGuid,
                    StowageChangeDataType.RotationCargo,
                    cargoPropertyChanges
                )
            );
        }

        return changes;
    };

    const createCargoTemperatureChange = (): StowagePlanChange | null => {
        if (!cargoTemperaturePropertyChanges.temperature) return null;

        const adjustedCargoTemperature: AdjustedCargoTemperature = {
            adjustedCargoTemperatureGuid: crypto.randomUUID(),
            cargo: cargoOperationVM.cargo,
            cargoGuid: cargoOperationVM.cargoGuid,
            temperature: cargoTemperaturePropertyChanges.temperature,
        };

        const response = updateAdjustedCargoTemperature(activeStowagePlan, adjustedCargoTemperature);

        return createStowagePlanChange(
            activeStowagePlan.stowagePlanGuid,
            response.adjustedCargoTemperatureGuid,
            StowageChangeDataType.AdjustedCargoTemperature,
            response.changedData
        );
    };

    const onCorrectedLitreWeightChange = (correctedLitreWeight: number) => {
        if (cargoOperationVM && correctedLitreWeight > 0) {
            updateNominatedVolumeByWeight(cargoOperationVM);
            setCargoOperationVM({ ...cargoOperationVM });
        }
    };

    const onClearUnloadingPort = () => {
        cargoOperationVM.unloadingPortCall = null;
        cargoOperationVM.unloadingBerthCall = null;

        setCargoOperationVM({ ...cargoOperationVM });
    };

    return (
        cargoOperationVM && (
            <OdfjellPanel
                headerTitle={isNewCargoOperation ? 'Add new cargo operation' : 'Edit cargo operation'}
                extHeaderTitle={getFormattedCargoNumber(
                    cargoOperationVM.cargo.voyageNumber,
                    cargoOperationVM.cargo.cargoNumber,
                    isManualCargo(cargoOperationVM.cargo)
                )}
                defaultPosition="center"
                panelWidth={PANEL_CONSTANTS.VERY_LARGE_WIDTH}
                onCancel={onCancel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={isFormDataInvalid()}
                actionButtonText="Save"
                preventOutsideClose={true}
                hasCurtain={true}
            >
                <CommoditySection cargoOperationVM={cargoOperationVM} isFieldReadOnly={isFieldReadOnly} onTradeNameChange={onTradeNameChange} />
                <CargoSection cargoOperationVM={cargoOperationVM} isFieldReadOnly={isFieldReadOnly} onCargoInputChange={onCargoInputChange} />
                <OperationSection
                    cargoOperationVM={cargoOperationVM}
                    isFieldReadOnly={isFieldReadOnly}
                    onOperationInputChange={onOperationInputChange}
                    onSelectUnloadingPort={onSelectUnloadingPort}
                    onSelectUnloadingPortForOperation={onSelectUnloadingPortForOperation}
                    onClearUnloadingPort={onClearUnloadingPort}
                    getUnloadingCall={getUnloadingCall}
                    getSelectedItemForOperation={getSelectedItemForOperation}
                    isNewCargoOperation={isNewCargoOperation}
                    unloadingOperations={(() => {
                        const operations = getUnloadingOperations(activeStowagePlan, cargoOperationVM.cargo);
                        return operations;
                    })()}
                    upcomingPortCalls={getUpcomingPortCalls()}
                />
                <PhysicalDataSection
                    cargoOperationVM={cargoOperationVM}
                    isFieldReadOnly={isFieldReadOnly}
                    onProductInputChange={onProductInputChange}
                    onCargoInputChange={onCargoInputChange}
                    isHeatingSectionVisible={isHeatingSectionVisible}
                    toggleHeatingSectionVisible={toggleHeatingSectionVisible}
                    onCorrectedLitreWeightChange={onCorrectedLitreWeightChange}
                />
                <CommentsSection cargoOperationVM={cargoOperationVM} isFieldReadOnly={isFieldReadOnly} onCargoInputChange={onCargoInputChange} />
            </OdfjellPanel>
        )
    );
};

export default CargoOperationPanel;
